<template>
  <div class="space-y-6">
    <!-- Summary Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div class="bg-card rounded-lg border p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-muted-foreground">Total Positions</p>
            <p class="text-2xl font-bold">{{ positionsData.count }}</p>
          </div>
          <div
            class="h-8 w-8 rounded bg-blue-100 dark:bg-blue-900 flex items-center justify-center"
          >
            <svg
              class="h-4 w-4 text-blue-600 dark:text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              ></path>
            </svg>
          </div>
        </div>
      </div>

      <div class="bg-card rounded-lg border p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-muted-foreground">Total Cash</p>
            <p class="text-2xl font-bold">
              ${{
                positionsData.total_cash.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              }}
            </p>
          </div>
          <div
            class="h-8 w-8 rounded bg-green-100 dark:bg-green-900 flex items-center justify-center"
          >
            <svg
              class="h-4 w-4 text-green-600 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
              ></path>
            </svg>
          </div>
        </div>
      </div>

      <div class="bg-card rounded-lg border p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-muted-foreground">Total Premium</p>
            <p class="text-2xl font-bold">
              ${{
                positionsData.total_premium.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              }}
            </p>
          </div>
          <div
            class="h-8 w-8 rounded bg-purple-100 dark:bg-purple-900 flex items-center justify-center"
          >
            <svg
              class="h-4 w-4 text-purple-600 dark:text-purple-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
              ></path>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Position Form -->
    <div class="bg-card rounded-lg border p-6">
      <h3 class="text-lg font-semibold mb-4">Add New Position</h3>

      <form
        @submit.prevent="createPosition"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4"
      >
        <div class="space-y-2">
          <label for="side" class="text-sm font-medium">Side</label>
          <select
            id="side"
            v-model="positionSide"
            class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="long">Long (Buy)</option>
            <option value="short">Short (Sell)</option>
          </select>
        </div>

        <div class="space-y-2">
          <label for="symbol" class="text-sm font-medium">Symbol</label>
          <SymbolSelector
            v-model="newPosition.symbol"
            placeholder="Search for a symbol..."
            @update:modelValue="calculateLiveValues"
          />
        </div>

        <div class="space-y-2">
          <label for="stock_qty" class="text-sm font-medium">Stock Qty</label>
          <input
            id="stock_qty"
            v-model.number="newPosition.stock_qty"
            type="number"
            min="1"
            placeholder="100"
            class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            required
            @input="calculateLiveValues"
          />
        </div>

        <div class="space-y-2">
          <label for="purchase_price" class="text-sm font-medium">Purchase Price</label>
          <input
            id="purchase_price"
            v-model.number="newPosition.purchase_price"
            type="number"
            step="0.01"
            min="0.01"
            placeholder="183.16"
            class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            required
            @input="calculateLiveValues"
          />
        </div>

        <div class="space-y-2">
          <label for="strike_price" class="text-sm font-medium">
            Strike Price <span class="text-muted-foreground text-xs">(optional)</span>
          </label>
          <input
            id="strike_price"
            v-model.number="newPosition.strike_price"
            type="number"
            step="0.01"
            min="0"
            placeholder="100.00"
            class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            @input="calculateLiveValues"
          />
        </div>

        <div class="space-y-2">
          <label for="premium_mark" class="text-sm font-medium">
            Premium Mark <span class="text-muted-foreground text-xs">(optional)</span>
          </label>
          <input
            id="premium_mark"
            v-model.number="newPosition.premium_mark"
            type="number"
            step="0.01"
            min="0"
            placeholder="4.20"
            class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            @input="calculateLiveValues"
          />
        </div>

        <div class="space-y-2">
          <label for="expire_date" class="text-sm font-medium">
            Expire Date <span class="text-muted-foreground text-xs">(optional)</span>
          </label>
          <DatePicker
            id="expire_date"
            v-model="newPosition.expire_date"
            placeholder="Select date..."
          />
        </div>

        <div class="md:col-span-2 lg:col-span-5 flex items-center justify-between">
          <!-- Live Calculations Display -->
          <div class="flex items-center space-x-6 text-sm">
            <div class="flex items-center space-x-2">
              <span class="text-muted-foreground">Cash:</span>
              <span class="font-medium text-green-600"
                >${{
                  liveCash.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }}</span
              >
            </div>
            <div class="flex items-center space-x-2">
              <span class="text-muted-foreground">Premium:</span>
              <span class="font-medium text-purple-600"
                >${{
                  livePremium.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }}</span
              >
            </div>
          </div>

          <button
            type="submit"
            :disabled="isCreating"
            class="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4"
          >
            <svg
              v-if="isCreating"
              class="mr-2 h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ isCreating ? "Creating..." : "Add Position" }}
          </button>
        </div>
      </form>
    </div>

    <!-- Positions List -->
    <div class="bg-card rounded-lg border">
      <div class="p-6 border-b">
        <h3 class="text-lg font-semibold">Your Positions</h3>
      </div>

      <div v-if="isLoading" class="p-6 text-center">
        <svg
          class="mx-auto h-8 w-8 animate-spin text-muted-foreground"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p class="mt-2 text-sm text-muted-foreground">Loading positions...</p>
      </div>

      <div v-else-if="positionsData.positions.length === 0" class="p-6 text-center">
        <div
          class="h-12 w-12 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center"
        >
          <svg
            class="h-6 w-6 text-muted-foreground"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            ></path>
          </svg>
        </div>
        <p class="text-sm text-muted-foreground">
          No positions yet. Create your first position above.
        </p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="border-b bg-muted/50">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Symbol
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Qty
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Purchase Price
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Purchase Price
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Strike
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Current Price
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Premium Mark
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Expire Date
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Cash
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Premium
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Price Change
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Current Value
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Unrealized P&L
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Absolute
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <tr
              v-for="position in positionsData.positions"
              :key="position.id"
              class="hover:bg-muted/50"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <router-link
                    :to="{ name: 'symbol-detail', params: { id: position.symbol } }"
                    class="text-sm font-medium text-primary hover:underline"
                  >
                    {{ position.symbol }}
                  </router-link>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                {{ position.stock_qty.toLocaleString() }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                ${{ position.purchase_price.toFixed(2) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span v-if="position.strike_price !== null && position.strike_price !== undefined">
                  ${{ position.strike_price.toFixed(2) }}
                </span>
                <span v-else class="text-muted-foreground">--</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span v-if="position.current_price" class="font-medium"
                  >${{ position.current_price.toFixed(2) }}</span
                >
                <span v-else class="text-muted-foreground">--</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span v-if="position.premium_mark !== null && position.premium_mark !== undefined">
                  ${{ position.premium_mark.toFixed(2) }}
                </span>
                <span v-else class="text-muted-foreground">--</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span v-if="position.expire_date">
                  {{ formatDate(position.expire_date) }}
                </span>
                <span v-else class="text-muted-foreground">--</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                ${{
                  position.cash.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-purple-600">
                ${{
                  position.premium.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span
                  v-if="position.price_change_percent !== undefined"
                  :class="
                    position.price_change_percent >= 0 ? 'text-green-600' : 'text-red-600'
                  "
                  class="font-medium"
                >
                  {{ position.price_change_percent >= 0 ? "+" : ""
                  }}{{ position.price_change_percent.toFixed(2) }}%
                </span>
                <span v-else class="text-muted-foreground">--</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span v-if="position.current_value" class="font-medium"
                  >${{
                    position.current_value.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }}</span
                >
                <span v-else class="text-muted-foreground">--</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span
                  v-if="position.current_value !== undefined"
                  :class="
                    position.current_value - position.cash + position.premium >= 0
                      ? 'text-green-600'
                      : 'text-red-600'
                  "
                  class="font-medium"
                >
                  {{
                    position.current_value - position.cash + position.premium >= 0
                      ? "+"
                      : ""
                  }}${{
                    (
                      position.current_value -
                      position.cash +
                      position.premium
                    ).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }}
                </span>
                <span v-else class="text-muted-foreground">--</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span v-if="position.current_value !== undefined" class="font-medium">
                  ${{
                    (position.current_value + position.premium).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }}
                </span>
                <span v-else class="text-muted-foreground">--</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <div class="flex items-center space-x-3">
                  <button
                    @click="openEditModal(position)"
                    class="text-blue-600 hover:text-blue-800"
                  >
                    Edit
                  </button>
                  <button
                    @click="openCloseModal(position)"
                    :disabled="isDeleting"
                    class="text-green-600 hover:text-green-800 disabled:opacity-50"
                  >
                    Close
                  </button>
                  <button
                    @click="deletePosition(position.id)"
                    :disabled="isDeleting"
                    class="text-red-600 hover:text-red-800 disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot class="border-t bg-muted/30">
            <tr class="font-semibold">
              <td class="px-6 py-3 text-sm">
                <span class="text-muted-foreground">TOTALS</span>
              </td>
              <td class="px-6 py-3 text-sm">
                {{ totalQty.toLocaleString() }}
              </td>
              <td class="px-6 py-3 text-sm">${{ averagePurchasePrice.toFixed(2) }}</td>
              <td class="px-6 py-3 text-sm">--</td>
              <td class="px-6 py-3 text-sm">${{ averageCurrentPrice.toFixed(2) }}</td>
              <td class="px-6 py-3 text-sm">
                <span v-if="averagePremiumMark > 0">${{ averagePremiumMark.toFixed(2) }}</span>
                <span v-else class="text-muted-foreground">--</span>
              </td>
              <td class="px-6 py-3 text-sm">
                <span class="text-muted-foreground">--</span>
              </td>
              <td class="px-6 py-3 text-sm font-medium text-green-600">
                ${{
                  totalCash.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }}
              </td>
              <td class="px-6 py-3 text-sm font-medium text-purple-600">
                ${{
                  totalPremium.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }}
              </td>
              <td class="px-6 py-3 text-sm">
                <span
                  :class="totalPriceChange >= 0 ? 'text-green-600' : 'text-red-600'"
                  class="font-medium"
                >
                  {{ totalPriceChange >= 0 ? "+" : "" }}{{ totalPriceChange.toFixed(2) }}%
                </span>
              </td>
              <td class="px-6 py-3 text-sm font-medium">
                ${{
                  totalCurrentValue.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }}
              </td>
              <td class="px-6 py-3 text-sm">
                <span
                  :class="totalUnrealizedPnL >= 0 ? 'text-green-600' : 'text-red-600'"
                  class="font-medium"
                >
                  {{ totalUnrealizedPnL >= 0 ? "+" : "" }}${{
                    totalUnrealizedPnL.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }}
                </span>
              </td>
              <td class="px-6 py-3 text-sm font-medium">
                ${{
                  totalAbsolute.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }}
              </td>
              <td class="px-6 py-3 text-sm">
                <span class="text-muted-foreground">--</span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Closed Positions Section -->
    <div class="bg-card rounded-lg border">
      <div class="p-6 border-b flex justify-between items-center">
        <h3 class="text-lg font-semibold">Closed Positions</h3>
        <button
          @click="deleteAllClosedPositions"
          :disabled="isDeleting || closedPositionsData.positions.length === 0"
          class="text-sm text-red-600 hover:text-red-800 disabled:opacity-50 font-medium"
        >
          Clear All
        </button>
      </div>

      <div v-if="isLoadingClosed" class="p-6 text-center">
        <svg
          class="mx-auto h-8 w-8 animate-spin text-muted-foreground"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p class="mt-2 text-sm text-muted-foreground">Loading closed positions...</p>
      </div>

      <div v-else-if="closedPositionsData.positions.length === 0" class="p-6 text-center">
        <div
          class="h-12 w-12 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center"
        >
          <svg
            class="h-6 w-6 text-muted-foreground"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
        </div>
        <p class="text-sm text-muted-foreground">
          No closed positions yet.
        </p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="border-b bg-muted/50">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Symbol
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Qty
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Purchase Price
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Purchase Price
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Strike
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Closing Price
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Premium Mark
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Expire Date
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Cash
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Premium
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Close Type
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Closed Date
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Realized P&L
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <tr
              v-for="position in closedPositionsData.positions"
              :key="position.id"
              class="hover:bg-muted/50"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <router-link
                    :to="{ name: 'symbol-detail', params: { id: position.symbol } }"
                    class="text-sm font-medium text-primary hover:underline"
                  >
                    {{ position.symbol }}
                  </router-link>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                {{ position.stock_qty.toLocaleString() }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                ${{ position.purchase_price.toFixed(2) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <span v-if="position.closing_price"
                  >${{ position.closing_price.toFixed(2) }}</span
                >
                <span v-else class="text-muted-foreground">--</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span v-if="position.premium_mark !== null && position.premium_mark !== undefined">
                  ${{ position.premium_mark.toFixed(2) }}
                </span>
                <span v-else class="text-muted-foreground">--</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span v-if="position.expire_date">
                  {{ formatDate(position.expire_date) }}
                </span>
                <span v-else class="text-muted-foreground">--</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                ${{
                  position.cash.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-purple-600">
                ${{
                  position.premium.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                  :class="
                    position.close_type === 'expired'
                      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                  "
                >
                  {{ position.close_type === 'expired' ? 'Expired' : 'Closed' }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span v-if="position.closed_at">{{
                  formatDateTime(position.closed_at)
                }}</span>
                <span v-else class="text-muted-foreground">--</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span
                  v-if="position.realized_pnl !== null && position.realized_pnl !== undefined"
                  :class="
                    position.realized_pnl >= 0 ? 'text-green-600' : 'text-red-600'
                  "
                  class="font-medium"
                >
                  {{ position.realized_pnl >= 0 ? "+" : ""
                  }}${{
                    position.realized_pnl.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }}
                </span>
                <span v-else class="text-muted-foreground">--</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <button
                  @click="deletePosition(position.id)"
                  :disabled="isDeleting"
                  class="text-red-600 hover:text-red-800 disabled:opacity-50 text-xs font-medium"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
          <tfoot class="border-t bg-muted/30">
            <tr class="font-semibold">
              <td class="px-6 py-3 text-sm" colspan="9">
                <span class="text-muted-foreground">TOTALS</span>
              </td>
              <td class="px-6 py-3 text-sm">
                <span class="text-muted-foreground">{{ closedPositionsData.count }} closed</span>
              </td>
              <td class="px-6 py-3 text-sm">
                <span
                  :class="closedPositionsData.total_realized_pnl >= 0 ? 'text-green-600' : 'text-red-600'"
                  class="font-medium"
                >
                  {{ closedPositionsData.total_realized_pnl >= 0 ? "+" : "" }}${{
                    closedPositionsData.total_realized_pnl.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Edit Position Modal -->
    <EditPositionModal
      :is-open="isEditModalOpen"
      :position="selectedPosition"
      @close="closeEditModal"
      @updated="handlePositionUpdated"
    />

    <!-- Close Position Modal -->
    <ClosePositionModal
      :is-open="isCloseModalOpen"
      :position="selectedPosition"
      @close="closeCloseModal"
      @closed="handlePositionClosed"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed } from 'vue'
import { apiClient, type PositionCreate, type PositionsListResponse, type ClosedPositionsListResponse, type Position } from '@/lib/api'
import SymbolSelector from '@/components/SymbolSelector.vue'
import DatePicker from '@/components/DatePicker.vue'
import EditPositionModal from '@/components/EditPositionModal.vue'
import ClosePositionModal from '@/components/ClosePositionModal.vue'

// Reactive data
const isLoading = ref(false)
const isLoadingClosed = ref(false)
const isCreating = ref(false)
const isDeleting = ref(false)
const isEditModalOpen = ref(false)
const isCloseModalOpen = ref(false)
const selectedPosition = ref<Position | null>(null)
const positionsData = ref<PositionsListResponse>({
  positions: [],
  total_cash: 0,
  total_premium: 0,
  count: 0
})
const closedPositionsData = ref<ClosedPositionsListResponse>({
  positions: [],
  total_realized_pnl: 0,
  count: 0
})

// Side selection
const positionSide = ref<'long' | 'short'>('long')

// New position form
const newPosition = reactive<PositionCreate>({
  symbol: '',
  stock_qty: 0,
  purchase_price: 0,
  premium_mark: null,
  strike_price: null,
  expire_date: null,
  notes: null
})

// Live calculations
const liveCash = computed(() => {
  if (newPosition.stock_qty && newPosition.purchase_price) {
    const value = newPosition.stock_qty * newPosition.purchase_price
    return positionSide.value === 'short' ? -value : value
  }
  return 0
})

const livePremium = computed(() => {
  if (newPosition.stock_qty && newPosition.premium_mark) {
    return (positionSide.value === 'short' ? -newPosition.stock_qty : newPosition.stock_qty) * (newPosition.premium_mark || 0)
  }
  return 0
})

// ... (existing computed properties) ...

const createPosition = async () => {
  isCreating.value = true
  try {
    // Adjust quantity for short positions
    const payload = {
      ...newPosition,
      stock_qty: positionSide.value === 'short' ? -newPosition.stock_qty : newPosition.stock_qty
    }
    
    await apiClient.createPosition(payload)

    // Reset form
    Object.assign(newPosition, {
      symbol: '',
      stock_qty: 0,
      purchase_price: 0,
      premium_mark: null,
      strike_price: null,
      expire_date: null,
      notes: null
    })
    positionSide.value = 'long'

    // Reload positions
    await loadPositions()
  } catch (error) {
    console.error('Failed to create position:', error)
  } finally {
    isCreating.value = false
  }
}

// Table totals computed properties
const totalQty = computed(() => {
  return positionsData.value.positions.reduce((sum, position) => sum + position.stock_qty, 0)
})

const averagePurchasePrice = computed(() => {
  if (positionsData.value.positions.length === 0) return 0
  const totalValue = positionsData.value.positions.reduce((sum, position) =>
    sum + (position.purchase_price * position.stock_qty), 0)
  return totalValue / totalQty.value
})

const averageCurrentPrice = computed(() => {
  const validPositions = positionsData.value.positions.filter(p => p.current_price !== null)
  if (validPositions.length === 0) return 0
  const totalValue = validPositions.reduce((sum, position) =>
    sum + (position.current_price! * position.stock_qty), 0)
  const totalQtyValid = validPositions.reduce((sum, position) => sum + position.stock_qty, 0)
  return totalValue / totalQtyValid
})

const averagePremiumMark = computed(() => {
  if (positionsData.value.positions.length === 0) return 0
  const positionsWithPremium = positionsData.value.positions.filter(
    p => p.premium_mark !== null && p.premium_mark !== undefined
  )
  if (positionsWithPremium.length === 0) return 0
  const totalValue = positionsWithPremium.reduce((sum, position) =>
    sum + ((position.premium_mark || 0) * position.stock_qty), 0)
  const totalQtyWithPremium = positionsWithPremium.reduce((sum, position) => sum + position.stock_qty, 0)
  return totalQtyWithPremium > 0 ? totalValue / totalQtyWithPremium : 0
})

const totalCash = computed(() => {
  return positionsData.value.positions.reduce((sum, position) => sum + position.cash, 0)
})

const totalPremium = computed(() => {
  return positionsData.value.positions.reduce((sum, position) => sum + position.premium, 0)
})

const totalPriceChange = computed(() => {
  const validPositions = positionsData.value.positions.filter(p => p.price_change_percent !== undefined)
  if (validPositions.length === 0) return 0
  const weightedSum = validPositions.reduce((sum, position) =>
    sum + (position.price_change_percent! * position.stock_qty), 0)
  return weightedSum / totalQty.value
})

const totalCurrentValue = computed(() => {
  return positionsData.value.positions.reduce((sum, position) =>
    sum + (position.current_value || 0), 0)
})

const totalUnrealizedPnL = computed(() => {
  return positionsData.value.positions.reduce((sum, position) =>
    sum + ((position.current_value || 0) - position.cash + position.premium), 0)
})

const totalAbsolute = computed(() => {
  return positionsData.value.positions.reduce((sum, position) =>
    sum + ((position.current_value || 0) + position.premium), 0)
})

// Methods
const calculateLiveValues = () => {
  // This is handled by the computed properties above
  // The @input events trigger reactivity automatically
}

const loadPositions = async () => {
  isLoading.value = true
  try {
    positionsData.value = await apiClient.getPositions()
  } catch (error) {
    console.error('Failed to load positions:', error)
  } finally {
    isLoading.value = false
  }
}


const loadClosedPositions = async () => {
  isLoadingClosed.value = true
  try {
    closedPositionsData.value = await apiClient.getClosedPositions()
  } catch (error) {
    console.error('Failed to load closed positions:', error)
  } finally {
    isLoadingClosed.value = false
  }
}

const openCloseModal = (position: Position) => {
  selectedPosition.value = position
  isCloseModalOpen.value = true
}

const closeCloseModal = () => {
  isCloseModalOpen.value = false
  selectedPosition.value = null
}

const handlePositionClosed = async () => {
  // Reload both open and closed positions
  await loadPositions()
  await loadClosedPositions()
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatDateTime = (dateTimeString: string) => {
  return new Date(dateTimeString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const openEditModal = (position: Position) => {
  selectedPosition.value = position
  isEditModalOpen.value = true
}

const closeEditModal = () => {
  isEditModalOpen.value = false
  selectedPosition.value = null
}

const handlePositionUpdated = async () => {
  await loadPositions()
}

const deletePosition = async (positionId: string) => {
  if (!confirm('Are you sure you want to delete this position?')) {
    return
  }
  
  isDeleting.value = true
  try {
    await apiClient.deletePosition(positionId)
    await loadPositions()
  } catch (error) {
    console.error('Failed to delete position:', error)
    alert('Failed to delete position. Please try again.')
  } finally {
    isDeleting.value = false
  }
}

const deleteAllClosedPositions = async () => {
  if (!confirm('Are you sure you want to delete ALL closed positions? This cannot be undone.')) {
    return
  }
  
  isDeleting.value = true
  try {
    const result = await apiClient.deleteAllClosedPositions()
    alert(result.message)
    await loadClosedPositions()
  } catch (error) {
    console.error('Failed to delete closed positions:', error)
    alert('Failed to delete closed positions. Please try again.')
  } finally {
    isDeleting.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadPositions()
  loadClosedPositions()
})
</script>
